import { useState, useEffect, useRef } from 'react'
import { 
  getEmailBatches, 
  getEmailBatch, 
  createEmailBatch, 
  uploadEmailBatch,
  updateEmailBatch,
  deleteEmailBatch,
  addEmailBatchItem,
  updateEmailBatchItem,
  deleteEmailBatchItem,
  toggleEmailBatchItemSelection,
  selectAllEmailBatchItems
} from '@/services/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Plus, Upload, Trash2, Edit2, Save,
  Mail, Users, ChevronDown, ChevronUp,
  CheckSquare, Square, CheckCircle2
} from 'lucide-react'

/**
 * Emails Management Page
 * Allows users to create, edit, and manage reusable email batches
 */
export default function Emails() {
  const [batches, setBatches] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedBatch, setSelectedBatch] = useState(null)
  const [expandedBatch, setExpandedBatch] = useState(null)
  
  // Create/Edit dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [showAddItemDialog, setShowAddItemDialog] = useState(false)
  const [editingItem, setEditingItem] = useState(null)
  const [batchToDelete, setBatchToDelete] = useState(null)
  
  // Form states
  const [newBatchName, setNewBatchName] = useState('')
  const [newBatchDescription, setNewBatchDescription] = useState('')
  const [manualInput, setManualInput] = useState('')
  const [uploadFile, setUploadFile] = useState(null)
  const [createMode, setCreateMode] = useState('manual') // 'manual' or 'upload'
  
  // Item form states
  const [itemEmail, setItemEmail] = useState('')
  const [itemPassword, setItemPassword] = useState('')
  const [itemQuota, setItemQuota] = useState(1024)
  
  // Selection save feedback
  const [saveSuccess, setSaveSuccess] = useState(false)
  // Item delete confirmation
  const [itemToDelete, setItemToDelete] = useState(null)

  const fileInputRef = useRef(null)

  // Load batches on mount
  useEffect(() => {
    loadBatches()
  }, [])

  const loadBatches = async () => {
    setLoading(true)
    try {
      const data = await getEmailBatches()
      setBatches(data)
      setError(null)
    } catch (err) {
      setError('Failed to load email batches: ' + err.message)
    }
    setLoading(false)
  }

  const loadBatchDetails = async (id) => {
    try {
      console.log('Loading batch details for:', id)
      const data = await getEmailBatch(id)
      console.log('Batch details loaded:', data)
      setSelectedBatch(data)
      setExpandedBatch(id)
    } catch (err) {
      console.error('Error loading batch details:', err)
      setError('Failed to load batch details: ' + err.message)
    }
  }

  const handleCreateBatch = async () => {
    if (!newBatchName.trim()) return
    
    try {
      let items = []
      
      if (createMode === 'manual' && manualInput.trim()) {
        // Parse manual input
        items = parseManualInput(manualInput)
      }
      
      await createEmailBatch(
        newBatchName.trim(),
        newBatchDescription.trim(),
        items
      )
      
      // If upload mode, upload the file
      if (createMode === 'upload' && uploadFile) {
        await uploadEmailBatch(uploadFile, newBatchName.trim(), newBatchDescription.trim())
      }
      
      setShowCreateDialog(false)
      resetCreateForm()
      loadBatches()
    } catch (err) {
      setError('Failed to create batch: ' + err.message)
    }
  }

  const handleUpdateBatch = async () => {
    if (!selectedBatch || !newBatchName.trim()) return
    
    try {
      await updateEmailBatch(selectedBatch.id, {
        name: newBatchName.trim(),
        description: newBatchDescription.trim()
      })
      
      setShowEditDialog(false)
      resetCreateForm()
      loadBatches()
      if (expandedBatch === selectedBatch.id) {
        loadBatchDetails(selectedBatch.id)
      }
    } catch (err) {
      setError('Failed to update batch: ' + err.message)
    }
  }

  const handleDeleteBatch = async () => {
    if (!batchToDelete) return
    
    try {
      await deleteEmailBatch(batchToDelete)
      setBatchToDelete(null)
      loadBatches()
      if (expandedBatch === batchToDelete) {
        setExpandedBatch(null)
        setSelectedBatch(null)
      }
    } catch (err) {
      setError('Failed to delete batch: ' + err.message)
    }
  }

  const handleAddItem = async () => {
    if (!selectedBatch || !itemEmail.trim()) return
    
    try {
      await addEmailBatchItem(selectedBatch.id, {
        email: itemEmail.trim(),
        password: itemPassword,
        quotaMb: parseInt(itemQuota) || 1024
      })
      
      setShowAddItemDialog(false)
      resetItemForm()
      loadBatchDetails(selectedBatch.id)
      loadBatches() // Refresh count
    } catch (err) {
      setError('Failed to add item: ' + err.message)
    }
  }

  const handleUpdateItem = async () => {
    if (!selectedBatch || !editingItem || !itemEmail.trim()) return
    
    try {
      await updateEmailBatchItem(selectedBatch.id, editingItem.id, {
        email: itemEmail.trim(),
        password: itemPassword,
        quotaMb: parseInt(itemQuota) || 1024
      })
      
      setEditingItem(null)
      resetItemForm()
      loadBatchDetails(selectedBatch.id)
    } catch (err) {
      setError('Failed to update item: ' + err.message)
    }
  }

  const handleDeleteItem = async () => {
    if (!selectedBatch || !itemToDelete) return
    try {
      await deleteEmailBatchItem(selectedBatch.id, itemToDelete)
      setItemToDelete(null)
      loadBatchDetails(selectedBatch.id)
      loadBatches()
    } catch (err) {
      setError('Failed to delete item: ' + err.message)
    }
  }

  const parseManualInput = (input) => {
    const lines = input.trim().split('\n')
    const items = []
    let currentQuota = 1024
    
    for (const line of lines) {
      const trimmedLine = line.trim()
      if (!trimmedLine || trimmedLine.startsWith('#')) continue
      
      // Check for default quota directive
      if (trimmedLine.toLowerCase().startsWith('default:')) {
        const quota = parseInt(trimmedLine.split(':')[1])
        if (!isNaN(quota)) currentQuota = quota
        continue
      }
      
      // Parse email:password:quota format
      const parts = trimmedLine.split(':')
      if (parts.length >= 2) {
        const email = parts[0].trim()
        const password = parts[1].trim()
        const quota = parts.length >= 3 ? parseInt(parts[2]) : currentQuota
        
        if (email && password) {
          items.push({
            email,
            password,
            quotaMb: isNaN(quota) ? currentQuota : quota
          })
        }
      }
    }
    
    return items
  }

  const resetCreateForm = () => {
    setNewBatchName('')
    setNewBatchDescription('')
    setManualInput('')
    setUploadFile(null)
    setCreateMode('manual')
  }

  const resetItemForm = () => {
    setItemEmail('')
    setItemPassword('')
    setItemQuota(1024)
  }

  const startEditItem = (item) => {
    setEditingItem(item)
    setItemEmail(item.email)
    setItemPassword(item.password)
    setItemQuota(item.quotaMb)
    setShowAddItemDialog(true)
  }

  const startEditBatch = (batch) => {
    setSelectedBatch(batch)
    setNewBatchName(batch.name)
    setNewBatchDescription(batch.description || '')
    setShowEditDialog(true)
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    const date = new Date(dateStr)
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString()
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-foreground/90">Email Batches</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Manage reusable email lists for migrations
          </p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} size="sm" className="shrink-0">
          <Plus className="w-3.5 h-3.5 mr-1.5" />
          New Batch
        </Button>
      </div>

      {error && (
        <Alert variant="destructive" className="border-0 bg-destructive/8 text-destructive rounded-xl">
          <AlertDescription className="text-xs">{error}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-5 h-5 rounded-full border-2 border-primary border-t-transparent animate-spin" />
        </div>
      ) : batches.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="w-14 h-14 rounded-2xl soft-card flex items-center justify-center mb-4 !transform-none">
            <Mail className="w-6 h-6 text-primary/50" />
          </div>
          <p className="text-sm font-medium text-foreground/70">No email batches yet</p>
          <p className="text-xs text-muted-foreground mt-1">Create your first batch to store reusable email lists</p>
          <Button onClick={() => setShowCreateDialog(true)} variant="outline" size="sm" className="mt-4">
            <Plus className="w-3.5 h-3.5 mr-1.5" />
            Create Batch
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {batches.map(batch => (
            <div key={batch.id} className="soft-card rounded-2xl overflow-hidden !transform-none">
              {/* Batch header */}
              <div className="flex items-center justify-between px-5 py-3.5">
                <button
                  className="flex items-center gap-3 text-left flex-1 min-w-0"
                  onClick={() => {
                    if (expandedBatch === batch.id) {
                      setExpandedBatch(null)
                      setSelectedBatch(null)
                    } else {
                      loadBatchDetails(batch.id)
                    }
                  }}
                >
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <Users className="w-4 h-4 text-primary/70" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground/85">{batch.name}</p>
                    {batch.description && (
                      <p className="text-xs text-muted-foreground truncate">{batch.description}</p>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0 ml-2">
                    {batch.itemCount} email{batch.itemCount !== 1 ? 's' : ''}
                  </span>
                  <span className="text-muted-foreground shrink-0 ml-1">
                    {expandedBatch === batch.id
                      ? <ChevronUp className="w-4 h-4" />
                      : <ChevronDown className="w-4 h-4" />}
                  </span>
                </button>

                <div className="flex items-center gap-1 ml-3 shrink-0">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => startEditBatch(batch)}>
                    <Edit2 className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setBatchToDelete(batch.id)}>
                    <Trash2 className="w-3.5 h-3.5 text-destructive" />
                  </Button>
                </div>
              </div>

              {/* Expanded content */}
              {expandedBatch === batch.id && selectedBatch && (
                <div className="border-t border-white/40">
                  {/* Toolbar */}
                  <div className="flex items-center justify-between px-5 py-2.5 bg-black/[0.02]">
                    <div className="flex items-center gap-2">
                      {saveSuccess ? (
                        <span className="inline-flex items-center gap-1 text-[11px] chip-success px-2 py-0.5 rounded-full">
                          <CheckCircle2 className="w-3 h-3" />
                          Saved
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          <span className="font-medium text-foreground/80">
                            {selectedBatch.items?.filter(i => i.selected !== false).length || 0}
                          </span>
                          {' '}/ {selectedBatch.items?.length || 0} selected
                          <span className="ml-1 opacity-50">(auto-saves)</span>
                        </span>
                      )}
                    </div>
                    <div className="flex gap-1.5">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-6 text-xs px-2"
                        onClick={async () => {
                          try {
                            await selectAllEmailBatchItems(selectedBatch.id, true)
                            await loadBatchDetails(selectedBatch.id)
                            setSaveSuccess(true)
                            setTimeout(() => setSaveSuccess(false), 1500)
                          } catch (err) {
                            console.error('Error selecting all:', err)
                          }
                        }}
                      >
                        <CheckSquare className="w-3 h-3 mr-1" />
                        All
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-6 text-xs px-2"
                        onClick={async () => {
                          try {
                            await selectAllEmailBatchItems(selectedBatch.id, false)
                            await loadBatchDetails(selectedBatch.id)
                            setSaveSuccess(true)
                            setTimeout(() => setSaveSuccess(false), 1500)
                          } catch (err) {
                            console.error('Error deselecting all:', err)
                          }
                        }}
                      >
                        <Square className="w-3 h-3 mr-1" />
                        None
                      </Button>
                      <Button
                        size="sm"
                        className="h-6 text-xs px-2"
                        onClick={() => {
                          setEditingItem(null)
                          resetItemForm()
                          setShowAddItemDialog(true)
                        }}
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Add
                      </Button>
                    </div>
                  </div>

                  {/* Item table */}
                  <ScrollArea className="h-[260px]">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/40 bg-black/[0.02]">
                          <th className="text-left py-2 px-5 text-[11px] font-medium text-muted-foreground uppercase tracking-wider w-10"></th>
                          <th className="text-left py-2 px-3 text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Email</th>
                          <th className="text-left py-2 px-3 text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Password</th>
                          <th className="text-left py-2 px-3 text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Quota</th>
                          <th className="text-right py-2 px-5 text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/30">
                        {selectedBatch.items?.map(item => (
                          <tr
                            key={item.id}
                            className={`hover:bg-white/20 transition-colors ${item.selected === false ? 'opacity-40' : ''}`}
                          >
                            <td className="py-2.5 px-5">
                              <input
                                type="checkbox"
                                checked={item.selected !== false}
                                onChange={async () => {
                                  try {
                                    const newSelected = item.selected === false ? true : false
                                    await toggleEmailBatchItemSelection(selectedBatch.id, item.id, newSelected)
                                    await loadBatchDetails(selectedBatch.id)
                                    setSaveSuccess(true)
                                    setTimeout(() => setSaveSuccess(false), 1500)
                                  } catch (err) {
                                    console.error('Error toggling selection:', err)
                                  }
                                }}
                                className="w-3.5 h-3.5 rounded border-border accent-primary cursor-pointer"
                              />
                            </td>
                            <td className="py-2.5 px-3 font-mono text-xs text-foreground/75">{item.email}</td>
                            <td className="py-2.5 px-3 font-mono text-xs text-muted-foreground">
                              {'•'.repeat(Math.min(item.password?.length || 0, 8))}
                            </td>
                            <td className="py-2.5 px-3 text-xs text-muted-foreground">{item.quotaMb} MB</td>
                            <td className="py-2.5 px-5 text-right">
                              <div className="flex justify-end gap-1">
                                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => startEditItem(item)}>
                                  <Edit2 className="w-3 h-3" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setItemToDelete(item.id)}>
                                  <Trash2 className="w-3 h-3 text-destructive" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {(!selectedBatch.items || selectedBatch.items.length === 0) && (
                      <div className="flex flex-col items-center justify-center py-10 text-center text-muted-foreground">
                        <Mail className="w-6 h-6 mb-2 opacity-30" />
                        <p className="text-xs">No emails in this batch yet</p>
                      </div>
                    )}
                  </ScrollArea>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create Batch Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="pb-2">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-primary/10 rounded-xl flex items-center justify-center">
                <Mail className="w-4 h-4 text-primary" />
              </div>
              <div>
                <DialogTitle className="text-base">Create Email Batch</DialogTitle>
                <DialogDescription className="text-xs">
                  Create a reusable list of emails for migrations
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-foreground/70">Batch Name *</label>
                <Input
                  value={newBatchName}
                  onChange={(e) => setNewBatchName(e.target.value)}
                  placeholder="e.g., Marketing Team"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-foreground/70">Description</label>
                <Input
                  value={newBatchDescription}
                  onChange={(e) => setNewBatchDescription(e.target.value)}
                  placeholder="Optional description"
                />
              </div>
            </div>

            {/* Mode toggle */}
            <div className="flex gap-1 p-1 bg-black/5 rounded-lg w-fit">
              {['manual', 'upload'].map(m => (
                <button
                  key={m}
                  type="button"
                  className={`px-3.5 py-1.5 rounded-md text-xs font-medium transition-all ${
                    createMode === m
                      ? 'bg-white/80 shadow text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  onClick={() => setCreateMode(m)}
                >
                  {m === 'manual' ? 'Manual Entry' : 'Upload File'}
                </button>
              ))}
            </div>

            {createMode === 'manual' ? (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-foreground/70">Email Accounts</label>
                  <span className="text-[11px] text-muted-foreground">
                    {parseManualInput(manualInput).length} emails detected
                  </span>
                </div>
                <textarea
                  className="w-full min-h-[180px] p-3 rounded-xl font-mono text-xs resize-none glass-input focus:outline-none focus:ring-2 focus:ring-ring/60"
                  value={manualInput}
                  onChange={(e) => setManualInput(e.target.value)}
                  placeholder={`# Format: email:password:quota(MB)\ndefault:500\nadmin@company.com:AdminPass123:\nsupport@company.com:Support456:`}
                />
                <div className="flex gap-3 text-[11px] text-muted-foreground">
                  <span>Format: email:password:quota</span>
                  <span>· One per line</span>
                  <span>· Quota optional (MB)</span>
                </div>
              </div>
            ) : (
              <div className="border-2 border-dashed border-white/60 rounded-xl p-8 text-center hover:border-primary/40 transition-colors">
                <Upload className="w-8 h-8 mx-auto text-primary/40 mb-2.5" />
                <label className="cursor-pointer block">
                  <Input
                    type="file"
                    accept=".csv,.txt"
                    onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                    ref={fileInputRef}
                    className="hidden"
                  />
                  <span className="text-sm font-medium text-primary hover:underline">
                    Click to upload file
                  </span>
                  {uploadFile && (
                    <span className="block mt-1.5 text-xs text-muted-foreground">
                      Selected: {uploadFile.name}
                    </span>
                  )}
                </label>
                <p className="text-xs text-muted-foreground mt-2">
                  CSV: email, password, quota | TXT: email:password:quota
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => { setShowCreateDialog(false); resetCreateForm() }}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleCreateBatch}
              disabled={!newBatchName.trim() || (createMode === 'upload' && !uploadFile)}
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              Create Batch
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Batch Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader className="pb-2">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-primary/10 rounded-xl flex items-center justify-center">
                <Edit2 className="w-4 h-4 text-primary" />
              </div>
              <div>
                <DialogTitle className="text-base">Edit Batch</DialogTitle>
                <DialogDescription className="text-xs">Update batch details</DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-foreground/70">Batch Name</label>
              <Input value={newBatchName} onChange={(e) => setNewBatchName(e.target.value)} placeholder="Enter batch name" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-foreground/70">Description</label>
              <Input value={newBatchDescription} onChange={(e) => setNewBatchDescription(e.target.value)} placeholder="Optional description" />
            </div>
          </div>
          <DialogFooter className="gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => { setShowEditDialog(false); resetCreateForm() }}>Cancel</Button>
            <Button size="sm" onClick={handleUpdateBatch}>
              <Save className="w-3.5 h-3.5 mr-1.5" />
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Item Dialog */}
      <Dialog open={showAddItemDialog} onOpenChange={setShowAddItemDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader className="pb-2">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-primary/10 rounded-xl flex items-center justify-center">
                <Mail className="w-4 h-4 text-primary" />
              </div>
              <div>
                <DialogTitle className="text-base">{editingItem ? 'Edit Email' : 'Add Email'}</DialogTitle>
                <DialogDescription className="text-xs">
                  {editingItem ? 'Update email details' : 'Add a new email to this batch'}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-foreground/70">Email Address *</label>
              <Input value={itemEmail} onChange={(e) => setItemEmail(e.target.value)} placeholder="user@domain.com" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-foreground/70">Password *</label>
              <Input type="text" value={itemPassword} onChange={(e) => setItemPassword(e.target.value)} placeholder="Enter password" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-foreground/70">Quota (MB)</label>
              <Input type="number" value={itemQuota} onChange={(e) => setItemQuota(e.target.value)} placeholder="1024" />
              <p className="text-[11px] text-muted-foreground">Mailbox quota in megabytes</p>
            </div>
          </div>
          <DialogFooter className="gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => { setShowAddItemDialog(false); setEditingItem(null); resetItemForm() }}>Cancel</Button>
            <Button size="sm" onClick={editingItem ? handleUpdateItem : handleAddItem} disabled={!itemEmail.trim() || !itemPassword.trim()}>
              {editingItem ? 'Save Changes' : 'Add Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Batch Dialog */}
      <Dialog open={!!batchToDelete} onOpenChange={() => setBatchToDelete(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete email batch?</DialogTitle>
            <DialogDescription>
              This batch and all its email entries will be permanently removed. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setBatchToDelete(null)}>Cancel</Button>
            <Button variant="destructive" size="sm" onClick={handleDeleteBatch}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Item Dialog */}
      <Dialog open={!!itemToDelete} onOpenChange={() => setItemToDelete(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Remove email?</DialogTitle>
            <DialogDescription>
              This email entry will be removed from the batch.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setItemToDelete(null)}>Cancel</Button>
            <Button variant="destructive" size="sm" onClick={handleDeleteItem}>Remove</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
